import { Close, Search } from '@mui/icons-material';
import { Dialog, DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import { memo, useMemo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssistantResponse } from 'types';
import {
  Disclaimer,
  DropdownArrow,
  Input,
  InputArea,
  MenuOptionContent,
  MenuOptionDesc,
  MenuOptionTitle,
  ModeLabel,
  ModeSelect,
  ModeSelectorWrapper,
  SendButton,
  StyledMenuItem,
  Wrapper,
  AssistantList,
  AssistantDialogActions,
  AssistantDialogButton,
  AssistantPagination,
} from './Footer.styles';

const ASSISTANTS_PER_PAGE = 5;

interface Props {
  assistants: readonly AssistantResponse[];
  selectedAssistantId: string | null;
  showAssistantPicker: boolean;
  isInteractionDisabled: boolean;
  onAssistantChange: (id: string | null) => void;
  input: string;
  onInputChange: (val: string) => void;
  sending: boolean;
  onSend: () => void;
}

const FooterBase = ({
  assistants,
  selectedAssistantId,
  showAssistantPicker,
  isInteractionDisabled,
  onAssistantChange,
  input,
  onInputChange,
  sending,
  onSend,
}: Props) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [assistantSearch, setAssistantSearch] = useState('');
  const [assistantPage, setAssistantPage] = useState(1);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setAssistantPage(1);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setAssistantSearch('');
  };

  const handleSelectAssistant = (id: string) => {
    onAssistantChange(id);
    handleMenuClose();
  };

  const handleAssistantOptionKeyDown = (event: React.KeyboardEvent, id: string) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    handleSelectAssistant(id);
  };

  const handleAssistantSearchChange = (value: string) => {
    setAssistantSearch(value);
    setAssistantPage(1);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onSend();
  };

  const selectedAssistant = assistants.find((a) => a.id === selectedAssistantId);
  const filteredAssistants = useMemo(() => {
    const query = assistantSearch.trim().toLocaleLowerCase();
    if (!query) return assistants;

    return assistants.filter((assistant) => `${assistant.label} ${assistant.description ?? ''}`.toLocaleLowerCase().includes(query));
  }, [assistantSearch, assistants]);
  const assistantPageCount = Math.max(1, Math.ceil(filteredAssistants.length / ASSISTANTS_PER_PAGE));
  const visibleAssistantPage = Math.min(assistantPage, assistantPageCount);
  const paginatedAssistants = filteredAssistants.slice((visibleAssistantPage - 1) * ASSISTANTS_PER_PAGE, visibleAssistantPage * ASSISTANTS_PER_PAGE);

  return (
    <Wrapper>
      {showAssistantPicker && (
        <ModeSelectorWrapper>
          <ModeLabel>{t('user.chat.assistantLabel')}</ModeLabel>

          <div>
            <ModeSelect
              onClick={handleMenuClick}
              aria-label={t('user.chat.assistantLabel')}
              aria-controls={openMenu ? 'assistant-dialog' : undefined}
              aria-haspopup="dialog"
              aria-expanded={openMenu ? 'true' : undefined}>
              <span>{selectedAssistant?.label}</span>
              <DropdownArrow isOpen={openMenu}>▲</DropdownArrow>
            </ModeSelect>

            <Dialog id="assistant-dialog" open={openMenu} onClose={handleMenuClose} fullWidth maxWidth="sm" aria-labelledby="assistant-dialog-title">
              <DialogTitle id="assistant-dialog-title" sx={{ pr: 6, fontWeight: 700 }}>
                {t('user.chat.assistantLabel')}
                <IconButton aria-label={t('user.chat.closeTitle')} onClick={handleMenuClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  value={assistantSearch}
                  onChange={(event) => handleAssistantSearchChange(event.target.value)}
                  placeholder={t('common.search._')}
                  aria-label={t('common.search._')}
                  InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
                <AssistantList role="listbox" aria-label={t('user.chat.assistantLabel')}>
                  {paginatedAssistants.map((assistant) => (
                    <StyledMenuItem
                      key={assistant.id}
                      data-testid={`chatbot-assistant-option-${assistant.id}`}
                      isActive={selectedAssistantId === assistant.id}
                      onClick={() => handleSelectAssistant(assistant.id)}
                      onKeyDown={(event) => handleAssistantOptionKeyDown(event, assistant.id)}
                      role="option"
                      aria-selected={selectedAssistantId === assistant.id}
                      tabIndex={0}>
                      <MenuOptionContent>
                        <MenuOptionTitle>{assistant.label}</MenuOptionTitle>
                        {assistant.description !== null && <MenuOptionDesc>{assistant.description}</MenuOptionDesc>}
                      </MenuOptionContent>
                    </StyledMenuItem>
                  ))}
                </AssistantList>
                {assistantPageCount > 1 && (
                  <AssistantPagination
                    count={assistantPageCount}
                    page={visibleAssistantPage}
                    onChange={(_event, nextPage) => setAssistantPage(nextPage)}
                    size="small"
                    color="primary"
                    aria-label={t('user.chat.assistantLabel')}
                  />
                )}
              </DialogContent>
              <AssistantDialogActions>
                <AssistantDialogButton type="button" onClick={handleMenuClose}>
                  {t('common.cancel')}
                </AssistantDialogButton>
              </AssistantDialogActions>
            </Dialog>
          </div>
        </ModeSelectorWrapper>
      )}

      <InputArea>
        <Input
          type="text"
          placeholder={sending ? t('user.chat.thinking') : t('user.chat.inputPlaceholder')}
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending || isInteractionDisabled}
        />
        <SendButton
          onClick={onSend}
          disabled={sending || isInteractionDisabled}
          title={sending ? t('user.chat.sendingTitle') : t('user.chat.sendTitle')}>
          ➤
        </SendButton>
      </InputArea>

      <Disclaimer>{t('user.chat.aiDisclaimer')}</Disclaimer>
    </Wrapper>
  );
};

export const Footer = memo(FooterBase);
